import React, { useEffect, useState, useRef } from "react";
import MemberAreaFooter from "../components/MemberAreaFooter";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft, Shield, AlertTriangle, User, Users, Clock, Tag,
  Film, Image as ImageIcon, Play, Info, ExternalLink, Star
} from "lucide-react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import { api } from "../lib/api";
import { resolvePublicTrailer } from "../lib/publicTrailer";
import MessageCreatorWidget from "../components/MessageCreatorWidget";

export default function MembersAreaWatch() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [fullPlayback, setFullPlayback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordingDetected, setRecordingDetected] = useState(false);
  const [performers, setPerformers] = useState([]);
  const [showingTrailer, setShowingTrailer] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [selectedPerformer, setSelectedPerformer] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const playerRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkAccessAndLoad();
    setupDRMProtection();
  }, [videoId]);

  const checkAccessAndLoad = async () => {
    try {
      // Check for admin preview mode
      const adminPreview = searchParams.get("admin_preview");

      const previewResponse = await api.get(`/api/videos/${videoId}/preview`);
      const previewVideo = previewResponse.data;
      const publicTrailer = resolvePublicTrailer(previewVideo);

      if (!adminPreview && publicTrailer) {
        setVideo({
          ...previewVideo,
          trailer_mux_playback_id: publicTrailer.playbackId,
          mux_playback_id: null,
          mux_playback_policy: "public",
        });
        loadGallery(previewVideo);
        return;
      }

      if (adminPreview) {
        toast.success("🔓 Admin Preview Mode", { duration: 2000 });
        const res = await api.get(`/api/catalog/${videoId}`);
        setVideo(res.data);
        loadGallery(res.data);
        loadComments();
        loadLikes();
        if (res.data.cast_crm_ids) loadPerformerCards(res.data.cast_crm_ids);
        setLoading(false);
        return;
      }
      
      // Regular member flow
      const memberToken = localStorage.getItem("memberToken");
      
      if (!memberToken) {
        toast.error("Please log in to watch this video");
        navigate("/members/login");
        return;
      }

      // Verify subscription
      const subRes = await api.get("/api/subscriptions/me", {
        headers: { Authorization: `Bearer ${memberToken}` }
      });
      
      const subscription = subRes.data;
      const userTier = subscription.tier;

      const videoData = previewVideo;
      
      // Check access
      const visibility = videoData.visibility || [];
      const hasAccess = userTier === "vip" 
        ? (visibility.includes("members") || visibility.includes("vip"))
        : visibility.includes("members");
      
      if (!hasAccess) {
        toast.error("You don't have access to this video.");
        navigate("/members-area");
        return;
      }

      setVideo(videoData);
      loadGallery(videoData);
      loadComments();
      loadLikes();
      
      // Load performers - check both cast_crm_ids and cast string
      if (videoData.cast_crm_ids && videoData.cast_crm_ids.length > 0) {
        console.log('Loading performers from cast_crm_ids:', videoData.cast_crm_ids);
        loadPerformerCards(videoData.cast_crm_ids);
      } else if (videoData.cast) {
        console.log('Loading performers from cast string:', videoData.cast);
        loadPerformerCards([]); // Will trigger fallback to parse cast string
      } else {
        console.log('No cast information available');
      }
      
    } catch (err) {
      console.error("Failed to load video:", err);
      if (err?.response?.status === 401) {
        toast.error("Please log in to watch this video");
        navigate("/members/login");
      } else {
        toast.error("Failed to load video");
        navigate("/members-area");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGallery = (videoData) => {
    // Load scene_photos as gallery
    if (videoData.scene_photos && videoData.scene_photos.length > 0) {
      setGallery(videoData.scene_photos);
    }
  };

  const loadPerformerCards = async (castIds) => {
    if (!castIds || castIds.length === 0) {
      console.log('No cast IDs provided');
      // If no cast_crm_ids but there's a cast string, parse it
      if (video && video.cast) {
        const castNames = video.cast.split(',').map(name => name.trim()).filter(n => n);
        const mockPerformers = castNames.map((name, idx) => ({
          id: `mock-${idx}`,
          stage_name: name,
          legal_name: name,
          type: "Porn Actor",
        }));
        console.log('Using parsed cast names:', mockPerformers);
        setPerformers(mockPerformers);
      }
      return;
    }
    
    try {
      const res = await api.get("/api/crm");
      const allContacts = res.data;
      
      const castPerformers = allContacts.filter(c => 
        castIds.includes(c.id) && c.type === "Porn Actor"
      );
      
      console.log('Found performers from CRM:', castPerformers.length);
      setPerformers(castPerformers);
    } catch (err) {
      console.error("Failed to load performers:", err);
    }
  };

  const setupDRMProtection = () => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      toast.error("Content is protected");
    };

    const handleKeyDown = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "u")
      ) {
        e.preventDefault();
        toast.error("Developer tools are disabled");
      }

      if (e.key === "PrintScreen") {
        e.preventDefault();
        toast.error("Screenshots are disabled");
      }
    };

    const detectRecording = () => {
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
        navigator.mediaDevices.getDisplayMedia = function() {
          setRecordingDetected(true);
          toast.error("Screen recording detected!", { duration: 5000 });
          return originalGetDisplayMedia.apply(this, arguments);
        };
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && playerRef.current) {
        playerRef.current.pause();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    detectRecording();

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  };

  const formatRuntime = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleWatchFull = async () => {
    const memberToken = localStorage.getItem("memberToken");
    if (!memberToken) {
      navigate("/members/login");
      return;
    }
    try {
      const response = await api.post(
        `/api/member-content/video/${videoId}/playback-token`,
        {},
        { headers: { Authorization: `Bearer ${memberToken}` } }
      );
      setFullPlayback(response.data);
      setShowingTrailer(false);
      toast.success("Now playing full video");
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/members/login");
        return;
      }
      toast.error(error.response?.data?.detail || "Could not authorize full playback");
    }
  };

  const loadComments = async () => {
    try {
      const res = await api.get(`/api/videos/${videoId}/comments`);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error("Failed to load comments:", err);
    }
  };

  const loadLikes = async () => {
    try {
      const res = await api.get(`/api/videos/${videoId}/likes`);
      setLikes(res.data.likes || 0);
    } catch (err) {
      console.error("Failed to load likes:", err);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      await api.post(`/api/videos/${videoId}/comments`, {
        video_id: videoId,
        content: newComment
      });
      setNewComment("");
      toast.success("Comment posted!");
      loadComments();
    } catch (err) {
      toast.error("Failed to post comment");
    }
  };

  const handleLike = async () => {
    if (hasLiked) {
      toast.info("You already liked this video");
      return;
    }

    try {
      const res = await api.post(`/api/videos/${videoId}/like`);
      setLikes(res.data.likes);
      setHasLiked(true);
      toast.success("❤️ Liked!");
    } catch (err) {
      toast.error("Failed to like video");
    }
  };

  const handleSendMessage = async () => {
    if (!messageSubject.trim() || !messageBody.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await api.post("/api/messages/send", {
        subject: messageSubject,
        message: messageBody
      });
      setShowMessageModal(false);
      setMessageSubject("");
      setMessageBody("");
      toast.success("Message sent to creator!");
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#000" }}>
        <div className="w-12 h-12 border-2 border-t-transparent border-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!video) return null;

  const useTrailer = showingTrailer && Boolean(video.trailer_mux_playback_id);
  const currentPlaybackId = useTrailer
    ? video.trailer_mux_playback_id
    : fullPlayback?.playback_id || video.mux_playback_id;
  const currentTokens = !useTrailer ? fullPlayback?.tokens : undefined;

  const currentDuration = showingTrailer && video.trailer_mux_duration
    ? video.trailer_mux_duration
    : video.mux_duration;

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a" }}>
      <MessageCreatorWidget />
      
      {/* Recording Warning */}
      {recordingDetected && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-6 py-3 flex items-center justify-center gap-2">
          <AlertTriangle size={20} />
          <span className="font-bold">SCREEN RECORDING DETECTED</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-zinc-900/80 backdrop-blur-sm border-b border-white/10 px-6 py-4 sticky top-0 z-40">
        <div className="container mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/members-area")}
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition">
            <ArrowLeft size={16} /> Back to Members Area
          </button>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Shield size={14} className="text-purple-400" />
            <span>DRM Protected</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Title & Quick Info */}
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">{video.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
              {currentDuration && (
                <div className="inline-flex items-center gap-1">
                  <Clock size={16} />
                  <span>{formatRuntime(currentDuration)}</span>
                </div>
              )}
              {video.category && (
                <div className="inline-flex items-center gap-1">
                  <Film size={16} />
                  <span>{video.category}</span>
                </div>
              )}
              {video.visibility && (
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-semibold uppercase">
                  {video.visibility.includes("vip") ? "VIP Exclusive" : "Members"}
                </span>
              )}
            </div>
          </div>

          {/* Video Player */}
          <div className="relative mb-8 rounded-2xl overflow-hidden" 
            style={{ 
              background: "linear-gradient(135deg, #1a0a2e 0%, #0f0519 100%)",
              boxShadow: "0 0 80px rgba(168, 85, 247, 0.3)"
            }}>
            
            {/* Trailer/Full Toggle Banner - PROMINENT */}
            {video.trailer_mux_playback_id && showingTrailer && (
              <>
                {/* Top Banner */}
                <div className="absolute top-4 left-4 z-10">
                  <span className="px-4 py-2 bg-yellow-500/90 backdrop-blur text-black font-bold rounded-lg text-sm inline-flex items-center gap-2 animate-pulse">
                    <Play size={16} />
                    WATCHING TRAILER
                  </span>
                </div>

                {/* Large Center Overlay - After trailer plays for 10s */}
                <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
                  <div className="pointer-events-auto text-center animate-fadeIn">
                    {/* Glassmorphism Card */}
                    <div 
                      className="px-8 py-6 rounded-2xl border-2 shadow-2xl backdrop-blur-xl"
                      style={{
                        background: "rgba(0, 0, 0, 0.85)",
                        borderColor: "rgba(168, 85, 247, 0.6)",
                        boxShadow: "0 0 60px rgba(168, 85, 247, 0.4), inset 0 0 20px rgba(168, 85, 247, 0.1)"
                      }}>
                      <div className="mb-4">
                        <Play size={48} className="mx-auto text-purple-400 mb-3" />
                        <h3 className="text-2xl font-bold text-white mb-2">
                          🎬 Trailer Playing
                        </h3>
                        <p className="text-zinc-300 text-sm mb-1">
                          You're watching the preview version
                        </p>
                        <p className="text-zinc-500 text-xs">
                          Click below to watch the full {formatRuntime(video.mux_duration || 0)} video
                        </p>
                      </div>
                      
                      <button
                        onClick={handleWatchFull}
                        className="w-full px-8 py-4 font-bold text-lg rounded-xl transition-all transform hover:scale-105 hover:shadow-2xl relative overflow-hidden group"
                        style={{
                          background: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
                          boxShadow: "0 8px 32px rgba(168, 85, 247, 0.4)"
                        }}>
                        <span className="relative z-10 flex items-center justify-center gap-3">
                          <Play size={24} className="animate-pulse" />
                          WATCH FULL VIDEO
                          <span className="text-2xl">→</span>
                        </span>
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 group-hover:translate-x-full transition-transform duration-1000" />
                      </button>

                      <p className="text-xs text-zinc-600 mt-3">
                        💡 Full video available for Members & VIP
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {currentPlaybackId ? (
              <MuxPlayer
                ref={playerRef}
                playbackId={currentPlaybackId}
                tokens={currentTokens}
                streamType="on-demand"
                autoPlay={showingTrailer}
                style={{ width: "100%", aspectRatio: "16/9" }}
                onLoadedData={() => {
                  if (recordingDetected && playerRef.current) {
                    playerRef.current.pause();
                  }
                }}
                onEnded={() => {
                  if (showingTrailer && video.mux_playback_id) {
                    // Auto-play full video after trailer
                    handleWatchFull();
                  }
                }}
              />
            ) : (
              <div className="aspect-video flex items-center justify-center">
                <p className="text-zinc-500">Video not available</p>
              </div>
            )}
          </div>

          {/* Description */}
          {video.description && (
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Info size={18} className="text-purple-400" />
                <h3 className="font-semibold text-white">About This Video</h3>
              </div>
              <p className="text-zinc-300 leading-relaxed">{video.description}</p>
            </div>
          )}

          {/* Cast Cards */}
          {performers.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="text-purple-400" size={22} />
                <h2 className="text-2xl font-bold text-white">Featured Cast</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {performers.map((performer) => (
                  <div 
                    key={performer.id}
                    onClick={() => setSelectedPerformer(performer)}
                    className="bg-zinc-900/70 border border-white/5 rounded-2xl overflow-hidden hover:border-purple-500/50 transition-all cursor-pointer group hover:scale-105 duration-300">
                    
                    {/* Performer Photo */}
                    <div className="relative aspect-[3/4] bg-zinc-800">
                      {performer.profile_picture ? (
                        <img 
                          src={performer.profile_picture}
                          alt={performer.stage_name || performer.legal_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="text-zinc-600" size={64} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                      
                      {/* Name Badge */}
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="font-bold text-white text-xl mb-1">
                          {performer.stage_name || performer.legal_name}
                        </h3>
                        {performer.country && (
                          <p className="text-xs text-zinc-300 flex items-center gap-1">
                            📍 {performer.country}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="p-4 space-y-3">
                      {/* Physical */}
                      {(performer.height || performer.age) && (
                        <div className="flex items-center gap-3 text-sm text-zinc-400">
                          {performer.age && <span>👤 {performer.age}y</span>}
                          {performer.height && <span>📏 {performer.height}</span>}
                        </div>
                      )}
                      
                      {/* Specialties */}
                      {performer.kinks && performer.kinks.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {performer.kinks.slice(0, 3).map((kink, idx) => (
                            <span key={idx}
                              className="px-2 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs rounded-md">
                              {kink}
                            </span>
                          ))}
                          {performer.kinks.length > 3 && (
                            <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-md">
                              +{performer.kinks.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* View Profile Button */}
                      <button className="w-full mt-2 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 font-semibold rounded-lg text-sm transition inline-flex items-center justify-center gap-2">
                        <Star size={14} />
                        View Full Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery / Scene Photos */}
          {gallery.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="text-purple-400" size={22} />
                <h2 className="text-2xl font-bold text-white">Scene Photos</h2>
                <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-full">
                  {gallery.length} photos
                </span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((photo, idx) => (
                  <div key={idx}
                    className="relative aspect-square bg-zinc-900 rounded-xl overflow-hidden group cursor-pointer hover:scale-105 transition-transform duration-300">
                    <img 
                      src={photo}
                      alt={`Scene ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ExternalLink className="text-white" size={32} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags & Category */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="text-purple-400" size={18} />
              <h3 className="font-semibold text-white">Tags & Categories</h3>
            </div>
            
            <div className="space-y-4">
              {video.category && (
                <div>
                  <span className="text-zinc-500 text-sm block mb-2">Category:</span>
                  <span className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-200 font-semibold rounded-lg inline-block">
                    {video.category}
                  </span>
                </div>
              )}
              
              {video.tags && video.tags.length > 0 && (
                <div>
                  <span className="text-zinc-500 text-sm block mb-2">Tags:</span>
                  <div className="flex flex-wrap gap-2">
                    {video.tags.map((tag, idx) => (
                      <span key={idx}
                        className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-lg transition cursor-pointer">
                        # {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-zinc-900/30 border border-white/5 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-white mb-4">Video Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {video.runtime_minutes && (
                <div>
                  <span className="text-zinc-500">Runtime:</span>
                  <span className="text-white ml-2 font-semibold">{video.runtime_minutes} minutes</span>
                </div>
              )}
              {video.shoot_date && (
                <div>
                  <span className="text-zinc-500">Shot:</span>
                  <span className="text-white ml-2">{new Date(video.shoot_date).toLocaleDateString()}</span>
                </div>
              )}
              {video.upload_date && (
                <div>
                  <span className="text-zinc-500">Published:</span>
                  <span className="text-white ml-2">{new Date(video.upload_date).toLocaleDateString()}</span>
                </div>
              )}
              {video.city && (
                <div>
                  <span className="text-zinc-500">Location:</span>
                  <span className="text-white ml-2">{video.city}</span>
                </div>
              )}
            </div>
          </div>

          {/* Likes & Comments Section */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 mb-8">
            {/* Like Button */}
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/5">
              <button
                onClick={handleLike}
                disabled={hasLiked}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition ${
                  hasLiked
                    ? "bg-red-500/20 text-red-400 cursor-not-allowed"
                    : "bg-red-500/10 hover:bg-red-500/20 text-red-400"
                }`}>
                <span className="text-2xl">{hasLiked ? "❤️" : "🤍"}</span>
                <span>{hasLiked ? "Liked" : "Like"}</span>
              </button>
              <span className="text-zinc-400 text-sm">{likes} likes</span>
            </div>

            {/* Comments */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Comments ({comments.length})</h3>
              
              {/* Post Comment */}
              <div className="mb-6">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 text-white placeholder-zinc-500 focus:border-purple-500 outline-none min-h-[100px] resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handlePostComment}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
                    Post Comment
                  </button>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="bg-zinc-800/30 border border-white/5 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                          {comment.member_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-white">{comment.member_name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              comment.member_tier === "vip" 
                                ? "bg-purple-500/20 text-purple-300" 
                                : "bg-pink-500/20 text-pink-300"
                            }`}>
                              {comment.member_tier.toUpperCase()}
                            </span>
                            <span className="text-zinc-500 text-xs">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-zinc-300 leading-relaxed">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Message Button */}
      <button
        onClick={() => setShowMessageModal(true)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full shadow-2xl flex items-center justify-center transition hover:scale-110 z-40"
        title="Message Creator">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </button>

      {/* Direct Message Modal */}
      {showMessageModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowMessageModal(false)}>
          <div 
            className="bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">Message Creator</h2>
              <p className="text-zinc-400 text-sm mt-1">Send a direct message about this video or any inquiries</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Subject</label>
                <input
                  type="text"
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                  placeholder="What's this about?"
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-white placeholder-zinc-500 focus:border-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Message</label>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder="Type your message here..."
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-white placeholder-zinc-500 focus:border-purple-500 outline-none min-h-[150px] resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSendMessage}
                  className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
                  Send Message
                </button>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performer Detail Modal */}
      {selectedPerformer && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPerformer(null)}>
          <div 
            className="bg-zinc-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            
            {/* Header Image */}
            <div className="relative aspect-video bg-zinc-800">
              {selectedPerformer.profile_picture ? (
                <img 
                  src={selectedPerformer.profile_picture}
                  alt={selectedPerformer.stage_name || selectedPerformer.legal_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="text-zinc-600" size={96} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h2 className="text-3xl font-bold text-white mb-2">
                  {selectedPerformer.stage_name || selectedPerformer.legal_name}
                </h2>
                {selectedPerformer.country && (
                  <p className="text-zinc-300">📍 {selectedPerformer.country}</p>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="p-6 space-y-6">
              {/* Bio */}
              {selectedPerformer.bio && (
                <div>
                  <h3 className="text-white font-semibold mb-2">About</h3>
                  <p className="text-zinc-300 leading-relaxed">{selectedPerformer.bio}</p>
                </div>
              )}

              {/* Physical Stats */}
              <div>
                <h3 className="text-white font-semibold mb-3">Stats</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedPerformer.age && (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <span className="text-zinc-500 block mb-1">Age</span>
                      <span className="text-white font-semibold">{selectedPerformer.age} years</span>
                    </div>
                  )}
                  {selectedPerformer.height && (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <span className="text-zinc-500 block mb-1">Height</span>
                      <span className="text-white font-semibold">{selectedPerformer.height}</span>
                    </div>
                  )}
                  {selectedPerformer.weight && (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <span className="text-zinc-500 block mb-1">Weight</span>
                      <span className="text-white font-semibold">{selectedPerformer.weight}</span>
                    </div>
                  )}
                  {selectedPerformer.ethnicity && (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <span className="text-zinc-500 block mb-1">Ethnicity</span>
                      <span className="text-white font-semibold">{selectedPerformer.ethnicity}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Specialties */}
              {selectedPerformer.kinks && selectedPerformer.kinks.length > 0 && (
                <div>
                  <h3 className="text-white font-semibold mb-3">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedPerformer.kinks.map((kink, idx) => (
                      <span key={idx}
                        className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm rounded-lg">
                        {kink}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {selectedPerformer.link_in_bio && (
                <div>
                  <a 
                    href={selectedPerformer.link_in_bio}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
                    <ExternalLink size={16} />
                    View Social Profile
                  </a>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setSelectedPerformer(null)}
                className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <MemberAreaFooter legacy />
    </div>
  );
}
